import styled from 'styled-components';

import {
  getGridBase,
  getThemeColor,
  getThemeTransition,
  media,
} from '@utils/styled';

import Button, { BUTTON_VARIANTS } from '@common/components/Button';
import CloseButton from '@common/components/CloseButton';
import Modal from '@common/components/Modal';
import {
  ContentWrapper,
  WrapperInner,
} from '@common/components/Modal/Modal.styled';
import Pill from '@common/components/Pill';
import Typography from '@common/components/Typography';
import { BODY_RESIZING_CLASS } from '@common/constants/body';
import { SIZES, VARIANTS } from '@common/constants/pills';

export const FiltersModal = styled(Modal)`
  && {
    top: ${({ transitionState }) =>
      ['entered', 'entering'].includes(transitionState) ? '0' : '100%'};
    background: ${getThemeColor('grey.100')};
    transition: ${getThemeTransition('top', 'verySlow')};
  }

  ${/* sc-sel */ WrapperInner} {
    ${getGridBase(12)};
    position: relative;
    overflow: auto;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    max-width: ${({ theme }) => theme.breakpoints[0]}rem;
    margin: 0 auto;
    ${({ theme }) => theme.getFluidSpacing('padding-left', 'widthBased', 0)};
    ${({ theme }) => theme.getFluidSpacing('padding-right', 'widthBased', 0)};
    background: ${getThemeColor('grey.100')};
  }

  ${/* sc-sel */ ContentWrapper} {
    margin: 0;
  }
`;

export const MobileWrapper = styled.div`
  ${getGridBase(12)};
  position: relative;
  width: auto;
  ${({ theme }) => theme.getFluidSpacing('padding-left', 'widthBased', 0)};
  ${({ theme }) => theme.getFluidSpacing('padding-right', 'widthBased', 0)};
  ${({ theme }) => theme.getFluidSpacing('margin-left', 'widthBased', 0, true)};
  ${({ theme }) =>
    theme.getFluidSpacing('margin-right', 'widthBased', 0, true)};
  transition: transform 1s ease;
  transform: translateX(
    ${({ transitionState }) =>
      ['exited', 'exiting'].includes(transitionState) ? '0%' : '-100%'}
  );

  .${BODY_RESIZING_CLASS} & {
    transition-property: color !important;
  }

  ${media.from960up`
    transform: translateX(0);
  `}
`;

export const MobileWrapperInner = styled.div`
  grid-column: span 12;

  ${media.from640up`
    grid-column: 2 / span 10;
  `}

  ${media.from960up`
    grid-column: span 12;
  `}
`;

export const TitleContainer = styled.div`
  position: sticky;
  top: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  height: 6rem;
  border-bottom: 1px solid ${getThemeColor('bluegrey.80')};
  background-color: ${getThemeColor('grey.100')};
`;

export const Title = styled(Typography).attrs({
  customAs: 'h2',
  variant: 'paragraph2',
})`
  display: inline-block;
  margin-bottom: 0;
  color: ${getThemeColor('bluegrey.15')};
`;

export const Counter = styled(Pill).attrs({
  size: SIZES.SMALL,
  variant: VARIANTS.LIGHT,
})`
  display: inline-flex;
  margin-left: 0.5rem;
`;

export const StyledCloseButton = styled(CloseButton)`
  margin-left: auto;
  border-color: transparent;
  background-color: transparent;
  ${({ theme }) => theme.getFluidSpacing('margin-right', 'equal', 5, true)};
`;

export const CategoriesContainer = styled.form`
  ${
    /* sc-block */ media.from960up`
    border-bottom: 1px solid ${getThemeColor('bluegrey.80')};
    display: flex;
  `
  }
`;

export const ButtonsRow = styled.div`
  position: ${({ transitionState }) =>
    ['entering', 'exited', 'exiting'].includes(transitionState)
      ? 'absolute'
      : 'fixed'};
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 5;
  display: flex;
  flex-direction: column-reverse;
  justify-content: center;
  align-items: center;
  border-top: 1px solid ${getThemeColor('bluegrey.80')};
  padding-top: 1.5rem;
  padding-bottom: calc(1.5rem + env(safe-area-inset-bottom));
  background-color: ${getThemeColor('grey.100')};
  ${({ theme }) =>
    theme.getFluidSpacing(
      'padding-left',
      'widthBased',
      0,
      false,
      'safe-area-inset-left',
    )};
  ${({ theme }) =>
    theme.getFluidSpacing(
      'padding-right',
      'widthBased',
      0,
      false,
      'safe-area-inset-right',
    )};

  ${media.from640up`
    flex-direction: row;
  `}
`;

export const SubmitButton = styled(Button).attrs({
  type: 'button',
  variant: BUTTON_VARIANTS.LEVEL_1_GREEN,
})`
  display: inline-block;
  width: 100%;

  ${media.from400up`
    width: auto;
  `}

  span {
    pointer-events: none;
  }
`;

export const ResetButton = styled(Button).attrs({
  type: 'button',
  variant: BUTTON_VARIANTS.LEVEL_2_BLUEGREY_90,
})`
  width: 100%;
  margin-bottom: 1rem;
  white-space: nowrap;

  ${media.from400up`
    width: auto;
  `}

  ${media.from640up`
    margin-bottom: 0;
    margin-left: 3rem;
  `}
`;

export const ResetButtonContainer = styled.div`
  overflow: hidden;
  width: 100%;
  height: ${({ transitionState }) =>
    ['entering', 'exited', 'exiting'].includes(transitionState) ? '0' : '6rem'};
  padding-bottom: ${({ transitionState }) =>
    ['entering', 'exited', 'exiting'].includes(transitionState) ? '0' : '1rem'};
  transition: ${getThemeTransition('all', 'slow')};

  ${media.from400up`
    width: auto;
  `}

  ${media.from640up`
    height: auto;
    max-width: ${({ transitionState }) =>
      ['entering', 'exited', 'exiting'].includes(transitionState)
        ? '0'
        : '28rem'};
    padding-bottom: 0;
    transition: ${getThemeTransition('max-width', 'slow')};
  `}
`;
